/**
 * ChangesView — the diff-mode body of the unified WorkspaceNav.
 *
 * Driven by a {@link ChangeSet} (not the live model): every changed entity
 * (module / interface / process / subprocess / view) becomes a row tagged with
 * its status sigil (+ ~ ↻ −), grouped under its source file. Unchanged entities
 * never appear; entities with no source file collect under a trailing "Other
 * changes" group. Selection runs through `onFocus(ref)` and highlights via the
 * caller-supplied `selected` ref.
 *
 * Styled with the SAME slate/Tailwind tokens as the rest of WorkspaceNav (not
 * the regular viewer's `.wh-*` CSS) so the diff rail reads identically to the
 * live rail — one component, one look, across viewer / editor / Studio.
 */
import { useMemo, useState, type ReactElement } from "react";
import { ChevronRight, File } from "lucide-react";
import type { ChangeSet, EntityRef, ModuleDiff, Status } from "@archlang/engine";
import { Tooltip, useContextMenu, type ContextMenuItem } from "@archlang/ui";
import { useTranslation } from "../../i18n";

export interface ChangesViewProps {
  readonly changeSet: ChangeSet;
  readonly selected: EntityRef | null;
  readonly onFocus: (ref: EntityRef) => void;
  /** Lower-cased search query (filters by row label). */
  readonly query?: string;
}

const cx = (...a: (string | false | null | undefined)[]): string =>
  a.filter(Boolean).join(" ");

const SIGIL: Record<Status, string> = {
  added: "+",
  modified: "~",
  renamed: "↻",
  removed: "−",
  unchanged: "",
};

const STATUS_COLOR: Record<Status, string> = {
  added: "text-emerald-400",
  modified: "text-amber-400",
  renamed: "text-sky-400",
  removed: "text-red-400",
  unchanged: "text-slate-500",
};

const STATUS_RANK: Record<Status, number> = {
  added: 0,
  modified: 1,
  renamed: 2,
  removed: 3,
  unchanged: 4,
};

const UNFILED = " other";

interface ChangeRow {
  readonly ref: EntityRef;
  readonly label: string;
  readonly status: Status;
  readonly kind: string;
  readonly file: string | null;
}

// `archspace://<repo>/<oid>/services/api.arch` → `services/api.arch`. The repo +
// oid segments differ between diff sides for the same file, so they're stripped
// before the path becomes a grouping key. Non-archspace inputs lose any leading
// slash; absent input → null.
function bareSourceFile(uri: string | undefined): string | null {
  if (!uri) return null;
  const m = /^archspace:\/\/[^/]+\/[^/]+\/(.*)$/.exec(uri);
  return m ? m[1] : uri.replace(/^\/+/, "");
}

function collectModule(m: ModuleDiff, out: ChangeRow[]): void {
  if (m.status !== "unchanged") {
    out.push({
      ref: { kind: "module", id: m.id },
      label: m.name,
      kind: "module",
      status: m.status,
      file: bareSourceFile(m.sourceFile),
    });
  }
  for (const i of m.interfaces.values()) {
    if (i.status === "unchanged") continue;
    out.push({
      ref: { kind: "interface", moduleId: m.id, name: i.name },
      label: `${m.name}.${i.name}`,
      kind: "interface",
      status: i.status,
      file: bareSourceFile(i.sourceFile ?? m.sourceFile),
    });
  }
  for (const c of m.children.values()) collectModule(c, out);
}

/** True when a row's ref is the selected entity. A selected interface also
 *  highlights its owning module row, and vice-versa, so the link is visible. */
function rowMatchesSelection(ref: EntityRef, sel: EntityRef | null): boolean {
  if (!sel) return false;
  const refModule = ref.kind === "module" ? ref.id : ref.kind === "interface" ? ref.moduleId : null;
  const selModule = sel.kind === "module" ? sel.id : sel.kind === "interface" ? sel.moduleId : null;
  if (refModule !== null && selModule !== null) {
    if (refModule !== selModule) return false;
    if (ref.kind === "interface" && sel.kind === "interface") return ref.name === sel.name;
    return ref.kind === sel.kind || sel.kind === "module" || ref.kind === "module";
  }
  if (ref.kind === "process" && sel.kind === "process") return ref.id === sel.id;
  if (ref.kind === "subprocess" && sel.kind === "subprocess") return ref.id === sel.id;
  if (ref.kind === "view" && sel.kind === "view") return ref.id === sel.id;
  return false;
}

export function ChangesView({ changeSet, selected, onFocus, query }: ChangesViewProps): ReactElement {
  const { t } = useTranslation("changes");
  const { t: tMenu } = useTranslation("nav");
  const menu = useContextMenu();
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const toggle = (key: string): void =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const rows = useMemo(() => {
    const all: ChangeRow[] = [];
    for (const m of changeSet.modules.values()) collectModule(m, all);
    for (const p of changeSet.processes.values()) {
      if (p.status === "unchanged") continue;
      const owner = p.ownerPath && p.ownerPath.length > 0 ? p.ownerPath.join(".") : null;
      all.push({
        ref: { kind: "process", id: p.id },
        label: owner ? `${owner}.${p.name}` : p.name,
        kind: "process",
        status: p.status,
        file: bareSourceFile(p.sourceFile),
      });
    }
    for (const sp of changeSet.subprocesses.values()) {
      if (sp.status === "unchanged") continue;
      const owner = sp.ownerPath && sp.ownerPath.length > 0 ? sp.ownerPath.join(".") : null;
      all.push({
        ref: { kind: "subprocess", id: sp.id },
        label: owner ? `${owner}.${sp.name}` : sp.name,
        kind: "subprocess",
        status: sp.status,
        file: bareSourceFile(sp.sourceFile),
      });
    }
    for (const v of changeSet.views.values()) {
      if (v.status === "unchanged") continue;
      all.push({
        ref: { kind: "view", id: v.id },
        label: v.id,
        kind: "view",
        status: v.status,
        file: bareSourceFile(v.sourceFile),
      });
    }
    return all;
  }, [changeSet]);

  const q = (query ?? "").trim().toLowerCase();
  const filtered = useMemo(
    () => (q ? rows.filter((r) => r.label.toLowerCase().includes(q)) : rows),
    [rows, q],
  );

  // Group by file (sorted within), fileless rows last under "Other changes".
  const groups = useMemo(() => {
    const byFile = new Map<string, ChangeRow[]>();
    for (const r of filtered) {
      const key = r.file ?? UNFILED;
      const arr = byFile.get(key);
      if (arr) arr.push(r);
      else byFile.set(key, [r]);
    }
    for (const arr of byFile.values()) {
      arr.sort(
        (a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status] || a.label.localeCompare(b.label),
      );
    }
    return [...byFile.entries()].sort(([a], [b]) =>
      a === UNFILED ? 1 : b === UNFILED ? -1 : a.localeCompare(b),
    );
  }, [filtered]);

  // Right-click menu for a changed-entity row: open (same as click) plus copy
  // its source path when one is known. No revert handler is in scope here (this
  // view is diff-only), so no destructive action.
  const itemsForRow = (r: ChangeRow): ContextMenuItem[] => {
    const items: ContextMenuItem[] = [{ label: tMenu("menu.open"), onClick: () => onFocus(r.ref) }];
    if (r.file) {
      items.push({
        label: tMenu("menu.copyPath"),
        onClick: () => void navigator.clipboard?.writeText(r.file as string),
      });
    }
    return items;
  };

  if (rows.length === 0) {
    return <div className="px-3 py-2 text-slate-600">{t("noChanges")}</div>;
  }
  if (filtered.length === 0) {
    return <div className="px-3 py-2 text-slate-600">{t("nothingMatches", { query })}</div>;
  }

  return (
    <div data-testid="changes-view">
      {groups.map(([file, items]) => {
        const open = !collapsed.has(file);
        const label = file === UNFILED ? t("otherChanges") : file;
        return (
          <div key={file}>
            <Tooltip content={label}>
              <button
                type="button"
                onClick={() => toggle(file)}
                aria-expanded={open}
                style={{ paddingLeft: 4 }}
                className="flex w-full items-center gap-1 truncate py-0.5 text-left text-slate-400 hover:bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
              >
                <span className="w-3 shrink-0 text-slate-500">
                  <ChevronRight
                    className={cx("h-3 w-3 transition-transform", open && "rotate-90")}
                    strokeWidth={2.2}
                    aria-hidden="true"
                  />
                </span>
                <File className="h-[13px] w-[13px] shrink-0 text-slate-400" strokeWidth={1.8} aria-hidden="true" />
                <span className="truncate font-medium">{label}</span>
                <span className="ml-auto shrink-0 pl-2 text-[10px] text-slate-600">{items.length}</span>
              </button>
            </Tooltip>
            {open &&
              items.map((r, i) => {
                const sel = rowMatchesSelection(r.ref, selected);
                return (
                  <Tooltip key={`${r.kind}:${r.label}:${i}`} content={r.label}>
                    <button
                      type="button"
                      onClick={() => onFocus(r.ref)}
                      onContextMenu={(e) => menu.open(e, itemsForRow(r))}
                      aria-current={sel ? "true" : undefined}
                      style={{ paddingLeft: 20 }}
                      className={cx(
                        "flex w-full items-center gap-1.5 truncate py-1 pr-2 text-left text-[13px] hover:bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950",
                        sel ? "bg-selection text-slate-100" : "text-slate-300",
                      )}
                    >
                      <span
                        aria-label={t(`status.${r.status}`)}
                        title={t(`status.${r.status}`)}
                        className={cx("w-3 shrink-0 text-center font-bold", STATUS_COLOR[r.status])}
                      >
                        {SIGIL[r.status]}
                      </span>
                      <span className={cx("truncate", r.status === "removed" && "line-through opacity-70")}>
                        {r.label}
                      </span>
                      <span className="ml-auto shrink-0 pl-2 text-[10px] uppercase tracking-wide text-slate-600">
                        {r.kind}
                      </span>
                    </button>
                  </Tooltip>
                );
              })}
          </div>
        );
      })}
      {menu.node}
    </div>
  );
}
